// The desk's rules, in one place for the games: every number here is the
// engine's own (the backend file is named next to each), so a game teaches
// exactly what the desk does. Games grade against these, never against
// what price did next.
import type { Candle } from "./types";

// ── Risk (backend services/dailyRiskGuard.ts, engineConfig.ts) ──────────────

/** Default risk per trade, % of the account (day_risk_config riskPerTradePct). */
export const RISK_PCT = 0.5;
/** The day stops after this many trades… */
export const MAX_TRADES_PER_DAY = 3;
/** …or once the day's closed trades lose this many R. */
export const MAX_DAILY_LOSS_R = 3;
/** A signal against BTC's strong trend trades at this share of the usual risk. */
export const AGAINST_BTC_RISK = 0.5;

// ── Plan gates (backend signals/evaluateSignal.ts) ──────────────────────────

/** Target 1 must pay at least this many times the risk. */
export const MIN_RR = 1.5;
/** Round-trip costs may eat at most this much of the risk (0.25R). */
export const MAX_COST_R = 0.25;
/** The setup's minimum confluence score. */
export const MIN_SCORE = 40;

// ── The trend-pullback setup (backend signals/setupClassifier.ts, setupChecklist.ts) ──

/** Price is in the pullback zone within this many ATR of the EMA 20 or EMA 50. */
export const ZONE_ATR = 1.5;
/** A real pullback retraced at least this many ATR from the recent extreme. */
export const PULLBACK_ATR = 0.8;
/** The stop goes this many ATR past the pullback's extreme (the last 8 candles' low for a buy). */
export const STOP_BUFFER_ATR = 0.25;
/** Candles the stop looks back over for the pullback's extreme. */
export const STOP_LOOKBACK = 8;
/** Target 1 without a structural level: 1.5R. Target 2: 2.5R. */
export const TP1_FALLBACK_R = 1.5;
export const TP2_R = 2.5;
/** A level closer than this many R in the way blocks the trade. */
export const MIN_ROOM_R = 0.5;
/** A trade with no progress is closed after this long (1h trend pullback). */
export const TIME_STOP_HOURS = 24;
/** RSI bands for a pullback entry: A grade, and the wider B+ grade. */
export const RSI_BAND = {
  LONG: { a: [35, 60], b: [30, 65] },
  SHORT: { a: [40, 65], b: [35, 70] },
} as const;
/** Volume on the trigger candle vs the 20-candle average: under 0.5× doesn't count, 1.2× or more is strong (A+). */
export const VOLUME_MIN = 0.5;
export const VOLUME_STRONG = 1.2;

// ── Entries (frontend chart/chartSignals.ts, backend openSignalStatus.ts) ───

/** Don't chase: skip the entry once price ran this many ATR past it. */
export const CHASE_ATR = 0.3;
/** A signal's entry is good for this many hours (two 1h candles), then it's stale. */
export const ENTRY_WINDOW_HOURS = 2;

// ── Costs (backend services/costModel.ts) ───────────────────────────────────

/** Binance USDT-perp taker fee per fill: 0.05%. */
export const FEE_PCT_PER_SIDE = 0.05;
/** Expected slippage per fill: 0.025%. */
export const SLIPPAGE_PCT_PER_SIDE = 0.025;
/** Funding per 8 hours: 0.01%. */
export const FUNDING_PCT_PER_8H = 0.01;

/** Round-trip cost as % of the position: two fills plus funding for the hours held. */
export function roundTripCostPct(holdingHours: number): number {
  return 2 * (FEE_PCT_PER_SIDE + SLIPPAGE_PCT_PER_SIDE) + (Math.max(0, holdingHours) / 8) * FUNDING_PCT_PER_8H;
}

/** Cost in R: the round trip divided by the stop distance (both as % of the entry). The engine assumes 6 hours held. */
export function costR(entry: number, stop: number, holdingHours = 6): number {
  const stopPct = (Math.abs(entry - stop) / entry) * 100;
  return stopPct > 0 ? roundTripCostPct(holdingHours) / stopPct : Infinity;
}

// ── Sessions (frontend daytrader/dayTrader.ts SESSION_HOURS, UTC) ───────────

export const SESSIONS = [
  { id: "asia", label: "Asia", from: 0, to: 7 },
  { id: "london", label: "London", from: 7, to: 13 },
  { id: "new-york", label: "New York", from: 13, to: 20 },
  { id: "late", label: "Late (US evening)", from: 20, to: 24 },
] as const;

// ── The 30-day plan (frontend desk/challenge.ts) ────────────────────────────

/** Trades before judging a strategy or adding money. */
export const GOAL_TRADES = 50;

// ── Trigger candles (backend signals/setupClassifier.ts, same definitions) ──

export const body = (c: Candle) => Math.abs(c.close - c.open);
export const upperWick = (c: Candle) => c.high - Math.max(c.open, c.close);
export const lowerWick = (c: Candle) => Math.min(c.open, c.close) - c.low;
export const isBullish = (c: Candle) => c.close > c.open;
export const isBearish = (c: Candle) => c.close < c.open;

export function isBullishEngulfing(prev: Candle, curr: Candle): boolean {
  return isBearish(prev) && isBullish(curr) && curr.close >= prev.open && curr.open <= prev.close;
}

export function isBearishEngulfing(prev: Candle, curr: Candle): boolean {
  return isBullish(prev) && isBearish(curr) && curr.close <= prev.open && curr.open >= prev.close;
}

/** Small body, lower wick at least 2× the body, upper wick at most half the body. */
export function isHammer(c: Candle): boolean {
  const b = body(c);
  return b > 0 && lowerWick(c) >= 2 * b && upperWick(c) <= b * 0.5;
}

/** Small body, upper wick at least 2× the body, lower wick at most half the body. */
export function isShootingStar(c: Candle): boolean {
  const b = body(c);
  return b > 0 && upperWick(c) >= 2 * b && lowerWick(c) <= b * 0.5;
}

export type Trigger = "bullish_engulfing" | "hammer" | "level_reclaim" | "bearish_engulfing" | "shooting_star";

/** The trend-pullback trigger candle: `last` judged against `prev` (backend reactionTrigger). */
export function reactionTrigger(prev: Candle, last: Candle, isLong: boolean): Trigger | null {
  if (isLong) {
    if (isBullishEngulfing(prev, last)) return "bullish_engulfing";
    if (isHammer(last)) return "hammer";
    if (last.close > prev.high) return "level_reclaim";
  } else {
    if (isBearishEngulfing(prev, last)) return "bearish_engulfing";
    if (isShootingStar(last)) return "shooting_star";
    if (last.close < prev.low) return "level_reclaim";
  }
  return null;
}

export const TRIGGER_WORDS: Record<Trigger, string> = {
  bullish_engulfing: "a bullish engulfing candle",
  hammer: "a hammer",
  level_reclaim: "a close above the last candle's high",
  bearish_engulfing: "a bearish engulfing candle",
  shooting_star: "a shooting star",
};

/** A trigger in words for its side: a sell's level reclaim is a close below the last candle's low. */
export function triggerWords(t: Trigger, isLong: boolean): string {
  return t === "level_reclaim" && !isLong ? "a close below the last candle's low" : TRIGGER_WORDS[t];
}

/** The range-fade trigger: a wick against the range edge longer than 1.2× the body. */
export function isRejectionWick(c: Candle, isLong: boolean): boolean {
  return isLong ? lowerWick(c) > 1.2 * body(c) : upperWick(c) > 1.2 * body(c);
}

/** The engine's stop for a pullback entry: past the last 8 candles' extreme by 0.25 ATR. */
export function pullbackStop(candles: Candle[], isLong: boolean, atrValue: number): number {
  const last = candles.slice(-STOP_LOOKBACK);
  return isLong
    ? Math.min(...last.map(c => c.low)) - STOP_BUFFER_ATR * atrValue
    : Math.max(...last.map(c => c.high)) + STOP_BUFFER_ATR * atrValue;
}
